import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { GetPreorderStatsResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { preordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get preorder stats.
 * @param servicesProviderId - Required services provider ID
 */
export const getPreorderStats = async (servicesProviderId: string): Promise<GetPreorderStatsResponse> => {
	const response = await apiClient.get<GetPreorderStatsResponse>(
		preordersEndpoints.stats(servicesProviderId),
	);
	return response.data;
};

/**
 * Hook for fetching preorder stats.
 * Uses the current services provider context and caches results for 30 seconds.
 */
export const useGetPreorderStats = (): UseQueryResult<GetPreorderStatsResponse, Error> => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.preorders.stats(servicesProviderId).queryKey,
		queryFn: () => getPreorderStats(servicesProviderId),
		enabled: Boolean(servicesProviderId),
		staleTime: 30_000, // 30 seconds - stats can be cached briefly
		...errorBoundaryOptions,
	});
};
